import { useEffect, useRef } from "react"

import {
  atom,
  DefaultValue,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil"
import type { SetterOrUpdater } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import {
  syncWaitingForEntityState,
  syncWaitingState,
} from "~/containers/Proposal/state/sync-state"
import type { GetSiteQuery, Imagery, PoisonedImagery } from "~/graphql/client"
import { StateCode, useGetSiteQuery } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { selectDiff } from "~/utils/recoil"

import { useHydrateImages } from "../../../LegacyDesigner/state/imageElements"
import { proposalLocalState } from "../proposal/proposal-state"

export const DEFAULT_SITE: GetSiteQuery[`site`] = {
  __typename: `Site`,
  id: ``,
  address: ``,
  latitude: 0,
  longitude: 0,
  streetViewLatitude: 0,
  streetViewLongitude: 0,
  createdAt: ``,
  updatedAt: ``,
  imagery: [],
  state: StateCode.PR,
  street: ``,
  locality: ``,
  postalCode: ``,
  postalCodeSuffix: ``,
  genabilityAccountId: null,
  isEnergyCommunity: false,
  isEnergyCommunityManuallySetAt: null,
}

export const siteRemoteState = atom<GetSiteQuery[`site`]>({
  key: `siteRemote`,
  default: DEFAULT_SITE,
})

export const siteLocalState = atom<GetSiteQuery[`site`]>({
  key: `siteLocal`,
  default: DEFAULT_SITE,
})

export const siteDiffState = selectDiff(siteLocalState, siteRemoteState)

export const positionState = selector<[number, number]>({
  key: `sitePosition`,
  get: ({ get }) => {
    const site = get(siteLocalState)

    return [site.latitude, site.longitude]
  },
  set: ({ set, get }, value) => {
    if (value instanceof DefaultValue) {
      set(siteLocalState, value)
    } else {
      const [latitude, longitude] = value
      const newSite = { ...get(siteLocalState), latitude, longitude }
      set(siteLocalState, newSite)
    }
  },
})

export const resetLocationZoomState = atom<number>({
  key: `resetLocationZoom`,
  default: 20,
})

export const shouldRetainZoomState = atom<boolean>({
  key: `shouldRetainZoom`,
  default: false,
})

export type SaveOptions = {
  repopulateImagery?: boolean
}

const siteImageryState = selector<GetSiteQuery[`site`][`imagery`]>({
  key: `siteImagery`,
  get: ({ get }) => {
    const site = get(siteRemoteState)

    return site.imagery
  },
})

export const poisonedSiteImageryState = selector<PoisonedImagery[]>({
  key: `poisonedSiteImagery`,
  get: ({ get }) => {
    const imagery = get(siteImageryState)

    return imagery
      .filter((image) => image.__typename === `PoisonedImagery`)
      .map((image) => image as PoisonedImagery)
  },
})

export const safeSiteImageryState = selector<Imagery[]>({
  key: `safeSiteImagery`,
  get: ({ get }) => {
    const imagery = get(siteImageryState)

    return imagery
      .filter((image) => image.__typename === `Imagery`)
      .map((image) => image as Imagery)
  },
})

export const useFetchSiteState = (
  proposalSlug?: string,
): [GetSiteQuery[`site`], SetterOrUpdater<GetSiteQuery[`site`]>, () => void] => {
  const { client } = useGraphqlClient()
  const { id } = useRecoilValue(proposalLocalState).site
  const lastId = useRef(id)
  const [site, setSite] = useRecoilState(siteRemoteState)
  const setLocal = useSetRecoilState(siteLocalState)
  const hydrateImages = useHydrateImages()
  const onError = useDispatchError()
  const shouldSyncImagery = useRecoilValue(syncWaitingForEntityState(`imagery`))
  const setSyncWaiting = useSetRecoilState(syncWaitingState)

  const { refetch } = useGetSiteQuery(
    client,
    { id },
    {
      enabled: !!id && !!proposalSlug,
      staleTime: Infinity,
      onSuccess: (data) => {
        setSite(data.site)
        setLocal(data.site)
      },
      onError,
    },
  )

  useEffect(() => {
    if (shouldSyncImagery && id && proposalSlug) {
      void refetch()
      setSyncWaiting((current) => ({ ...current, imagery: false }))
    }
  }, [refetch, id, proposalSlug, setSyncWaiting, shouldSyncImagery])

  const safeImages = useRecoilValue(safeSiteImageryState)

  useEffect(() => {
    hydrateImages(safeImages)
    // need to update createRecoilMapper to use a similar pattern as we are in useDesign.
    // do not fix warning until updating the mapper!!
  }, [safeImages])

  useEffect(() => {
    if (id && proposalSlug && id !== lastId.current) {
      void refetch()
    }
    lastId.current = id
  }, [id, proposalSlug, refetch])

  return [site, setSite, refetch]
}
